import React, { useState, useEffect } from "react";
import {
    get2FAStatus,
    setup2FA,
    enable2FA,
    disable2FA,
    regenerateBackupCodes
} from "../../api/authClient";

export default function TwoFactorSettings() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Setup state
    const [setupData, setSetupData] = useState(null);
    const [setupCode, setSetupCode] = useState("");
    const [setupLoading, setSetupLoading] = useState(false);

    // Disable state
    const [showDisable, setShowDisable] = useState(false);
    const [disableCode, setDisableCode] = useState("");
    const [disableLoading, setDisableLoading] = useState(false);

    // Backup codes state
    const [showBackupCodes, setShowBackupCodes] = useState(false);
    const [backupCodes, setBackupCodes] = useState([]);
    const [regenerateCode, setRegenerateCode] = useState("");
    const [regenerateLoading, setRegenerateLoading] = useState(false);

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        try {
            setLoading(true);
            const data = await get2FAStatus();
            setStatus(data);
        } catch (err) {
            setError("Failed to load 2FA status");
        } finally {
            setLoading(false);
        }
    };

    const handleStartSetup = async () => {
        try {
            setSetupLoading(true);
            setError("");
            const data = await setup2FA();
            setSetupData(data);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to start 2FA setup");
        } finally {
            setSetupLoading(false);
        }
    };

    const handleEnable2FA = async (e) => {
        e.preventDefault();
        try {
            setSetupLoading(true);
            setError("");
            const response = await enable2FA(setupCode);
            setBackupCodes(response.backupCodes || []);
            setShowBackupCodes(true);
            setSetupData(null);
            setSetupCode("");
            setSuccess("Two-factor authentication enabled successfully!");
            await fetchStatus();
        } catch (err) {
            setError(err.response?.data?.message || "Invalid verification code");
        } finally {
            setSetupLoading(false);
        }
    };

    const handleDisable2FA = async (e) => {
        e.preventDefault();
        try {
            setDisableLoading(true);
            setError("");
            await disable2FA(disableCode);
            setShowDisable(false);
            setDisableCode("");
            setSuccess("Two-factor authentication has been disabled");
            await fetchStatus();
        } catch (err) {
            setError(err.response?.data?.message || "Invalid verification code");
        } finally {
            setDisableLoading(false);
        }
    };

    const handleRegenerateBackupCodes = async (e) => {
        e.preventDefault();
        try {
            setRegenerateLoading(true);
            setError("");
            const response = await regenerateBackupCodes(regenerateCode);
            setBackupCodes(response.backupCodes || []);
            setShowBackupCodes(true);
            setRegenerateCode("");
            setSuccess("New backup codes generated!");
        } catch (err) {
            setError(err.response?.data?.message || "Failed to regenerate backup codes");
        } finally {
            setRegenerateLoading(false);
        }
    };

    const copyBackupCodes = () => {
        const codesText = backupCodes.join("\n");
        navigator.clipboard.writeText(codesText);
        setSuccess("Backup codes copied to clipboard");
    };

    if (loading) {
        return (
            <div className="card p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-gray-100 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-100 rounded w-2/3"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="card p-6">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h2>
                    <p className="text-sm text-gray-500">
                        Add an extra layer of security to your account
                    </p>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-danger-500">{error}</p>
                </div>
            )}
            {success && (
                <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                    <p className="text-sm text-accent-600">{success}</p>
                </div>
            )}

            {/* Status Badge */}
            <div className="flex items-center gap-2 mb-6">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    status?.enabled
                        ? "bg-accent-50 text-accent-600"
                        : "bg-gray-100 text-gray-500"
                }`}>
                    {status?.enabled ? "Enabled" : "Not Enabled"}
                </span>
                {status?.enabled && status?.remainingBackupCodes !== undefined && (
                    <span className="text-xs text-gray-500">
                        {status.remainingBackupCodes} backup codes remaining
                    </span>
                )}
            </div>

            {/* Backup Codes Display */}
            {showBackupCodes && backupCodes.length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h3 className="text-sm font-medium text-amber-600 mb-2">
                        Save Your Backup Codes
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">
                        Store these codes in a safe place. You can use them to access your account if you lose your authenticator device.
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-3 font-mono text-sm">
                        {backupCodes.map((code, i) => (
                            <div key={i} className="bg-white border border-gray-200 p-2 rounded text-center text-gray-800">
                                {code}
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={copyBackupCodes}
                            className="text-xs text-accent-600 hover:text-accent-700"
                        >
                            Copy all codes
                        </button>
                        <button
                            onClick={() => setShowBackupCodes(false)}
                            className="text-xs text-gray-500 hover:text-gray-900"
                        >
                            I've saved these codes
                        </button>
                    </div>
                </div>
            )}

            {/* Not Enabled - Setup Flow */}
            {!status?.enabled && !setupData && (
                <div>
                    <p className="text-sm text-gray-500 mb-4">
                        Protect your account by requiring a verification code from your phone in addition to your password.
                    </p>
                    <button
                        onClick={handleStartSetup}
                        disabled={setupLoading}
                        className="btn-primary"
                    >
                        {setupLoading ? "Loading..." : "Set Up Two-Factor Authentication"}
                    </button>
                </div>
            )}

            {/* Setup in Progress */}
            {setupData && (
                <div className="space-y-4">
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-900 mb-3">
                            1. Scan QR Code
                        </h3>
                        <p className="text-xs text-gray-500 mb-3">
                            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                        </p>
                        <div className="flex justify-center mb-3">
                            <img
                                src={`data:image/png;base64,${setupData.qrCodeBase64}`}
                                alt="2FA QR Code"
                                className="w-48 h-48 bg-white p-2 rounded"
                            />
                        </div>
                        <p className="text-xs text-gray-500 text-center">
                            Or enter this code manually: <code className="bg-gray-200 px-1 rounded">{setupData.secret}</code>
                        </p>
                    </div>

                    <form onSubmit={handleEnable2FA} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-900 mb-3">
                            2. Verify Code
                        </h3>
                        <p className="text-xs text-gray-500 mb-3">
                            Enter the 6-digit code from your authenticator app to verify setup
                        </p>
                        <input
                            type="text"
                            value={setupCode}
                            onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ""))}
                            maxLength={6}
                            placeholder="000000"
                            className="input w-full text-center text-2xl tracking-widest font-mono mb-3"
                        />
                        <div className="flex gap-2">
                            <button
                                type="submit"
                                disabled={setupLoading || setupCode.length !== 6}
                                className="btn-primary flex-1"
                            >
                                {setupLoading ? "Verifying..." : "Verify & Enable"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setSetupData(null); setSetupCode(""); }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Enabled - Manage Options */}
            {status?.enabled && !setupData && (
                <div className="space-y-4">
                    {/* Regenerate Backup Codes */}
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Backup Codes</h3>
                        <p className="text-xs text-gray-500 mb-3">
                            Generate new backup codes if you've lost access to your existing ones.
                            This will invalidate all previous backup codes.
                        </p>
                        <form onSubmit={handleRegenerateBackupCodes} className="flex gap-2">
                            <input
                                type="text"
                                value={regenerateCode}
                                onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, ""))}
                                maxLength={6}
                                placeholder="Enter 2FA code"
                                className="input flex-1 font-mono"
                            />
                            <button
                                type="submit"
                                disabled={regenerateLoading || regenerateCode.length !== 6}
                                className="btn-secondary"
                            >
                                {regenerateLoading ? "..." : "Generate New Codes"}
                            </button>
                        </form>
                    </div>

                    {/* Disable 2FA */}
                    {!showDisable ? (
                        <button
                            onClick={() => setShowDisable(true)}
                            className="text-sm text-danger-500 hover:text-danger-600"
                        >
                            Disable Two-Factor Authentication
                        </button>
                    ) : (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                            <h3 className="text-sm font-medium text-danger-500 mb-2">
                                Disable Two-Factor Authentication
                            </h3>
                            <p className="text-xs text-gray-500 mb-3">
                                This will make your account less secure. Enter your 2FA code to confirm.
                            </p>
                            <form onSubmit={handleDisable2FA} className="flex gap-2">
                                <input
                                    type="text"
                                    value={disableCode}
                                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                                    maxLength={6}
                                    placeholder="Enter 2FA code"
                                    className="input flex-1 font-mono"
                                />
                                <button
                                    type="submit"
                                    disabled={disableLoading || disableCode.length !== 6}
                                    className="px-4 py-2 bg-danger-500 text-white rounded-lg hover:bg-danger-600 disabled:opacity-50"
                                >
                                    {disableLoading ? "..." : "Disable"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowDisable(false); setDisableCode(""); }}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
